import * as z from "zod";

interface PartialSafeParseSuccess<T> {
    success: true;
    data: T;
}

interface PartialSafeParseFailure<T> {
    success: false;
    error: z.ZodError<T>;
    validData: Partial<T>;
}

type PartialSafeParseResult<T> =
    PartialSafeParseSuccess<T> | PartialSafeParseFailure<T>;

/**
 * Unwraps wrapper schemas (readonly, optional, ...) to find the underlying
 * object schema that supports .omit(). Brands add no runtime wrapper.
 */
function findOmittableSchema(schema: z.core.$ZodType): z.core.$ZodType {
    if (schema instanceof z.ZodObject) return schema;

    const { def } = schema._zod;
    if ("innerType" in def && def.innerType instanceof z.core.$ZodType)
        return findOmittableSchema(def.innerType);

    return schema;
}

/**
 * Replacement for zu.partialSafeParse from zod_utilz.
 * Parses input against a schema; on failure, returns partial valid data
 * by omitting fields that have validation errors.
 *
 * Accepts branded and readonly schemas directly — no need to unwrap manually.
 */
export function partialSafeParse<T extends z.ZodType>(
    schema: T,
    input: unknown,
): PartialSafeParseResult<z.output<T>> {
    const result = schema.safeParse(input);

    if (result.success) {
        return { success: true, data: result.data };
    }

    const { fieldErrors, formErrors } = z.flattenError(result.error);

    if (formErrors.length > 0) {
        return { success: false, error: result.error, validData: {} };
    }

    const inputObject = input as Record<string, unknown>;
    const invalidKeys = Object.keys(fieldErrors);

    const omitMask: Record<string, true> = Object.fromEntries(
        invalidKeys.map((key) => [key, true as const]),
    );

    const validInput = Object.fromEntries(
        Object.entries(inputObject).filter(
            ([key]) => !invalidKeys.includes(key),
        ),
    );

    const objectSchema = findOmittableSchema(schema);
    const omittedSchema =
        objectSchema instanceof z.ZodObject
            ? objectSchema.omit(omitMask)
            : objectSchema;
    const parseResult = z.safeParse(omittedSchema, validInput);

    return {
        success: false,
        error: result.error,
        validData: parseResult.success
            ? (parseResult.data as Partial<z.output<T>>)
            : {},
    };
}
